/* eslint-disable security/detect-unsafe-regex */
/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
/* eslint-disable react/no-unknown-property */
import { Module, Node, getTelemetryObject } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { ICheckoutViewProps, ILineItem, ILineItemDeliveryGroup, ILineItems, IPickUpAtStore } from './moe-checkout';
import { IEmailDelivery } from './components';
// import { IInvoicePaymentSummary } from './components/get-invoice-payment-summary';
import { SignInButton } from '../../components/signinbutton.component';
import { Address, CommerceProperty } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import { getDefaultCustomerDetailsAsync } from '../../actions/extensions/DataActionExtension.g';
import { getAsset } from '@msdyn365-commerce/core';
import { ACTION_BUTTONS, DOWNLOAD_KETE_SITE_LANGUAGE } from '../../utilities/constants';
import { getToggleLang, getUserPrefLang, getUserPreferredLanguage } from '../../utilities/language-change';

export const PickUpAtStoreComponent: React.FC<IPickUpAtStore> = ({ PickUpAtStore, label, location }) => (
    <Node {...PickUpAtStore}>
        {label}
        {location}
    </Node>
);

export const EmailDeliveryComponent: React.FC<IEmailDelivery> = ({ EmailDelivery, label }) => <Node {...EmailDelivery}>{label}</Node>;

export const LineItemComponent: React.FC<ILineItem> = ({ LineItem, item, pickUpAtStore, emailDelivery }) => (
    <Node {...LineItem}>
        {item}
        {pickUpAtStore && <PickUpAtStoreComponent {...pickUpAtStore} />}
        {emailDelivery && <EmailDeliveryComponent {...emailDelivery} />}
    </Node>
);

export const LineItemGroupComponent: React.FC<ILineItemDeliveryGroup> = ({ LineItemDeliveryGroup, LineItemList, heading, lineItems }) => (
    <Node {...LineItemDeliveryGroup}>
        {heading}
        <Node {...LineItemList}>
            {lineItems.map(lineItem => (
                <LineItemComponent key={lineItem.LineId} {...lineItem} />
            ))}
        </Node>
    </Node>
);

export const LineItemGroupComponentWithMultiplePickUp: React.FC<ILineItemDeliveryGroup> = ({
    LineItemDeliveryGroup,
    LineItemList,
    heading,
    lineItems,
    lineItemWraper,
    lineItemWraperIcon
}) => (
    <Node {...LineItemDeliveryGroup}>
        {lineItemWraperIcon}
        {lineItemWraper}
        {heading}
        <Node {...LineItemList}>
            {lineItems.map(lineItem => (
                <LineItemComponentWithMultiplePickUp key={lineItem.LineId} {...lineItem} />
            ))}
        </Node>
    </Node>
);

export const LineItemComponentWithMultiplePickUp: React.FC<ILineItem> = ({ LineItem, item, pickUpAtStore, emailDelivery }) => (
    <Node {...LineItem}>
        {item}
        {emailDelivery && <EmailDeliveryComponent {...emailDelivery} />}
    </Node>
);

export const PickUpAtStoreComponentWithMultiplePickUp: React.FC<IPickUpAtStore> = ({ PickUpAtStore, label, location }) => (
    <Node {...PickUpAtStore}>
        {label}
        {location}
    </Node>
);

export const LineItemsComponent: React.FC<ILineItems> = ({
    LineItems,
    Header,
    heading,
    editLink,
    itemsForPickup,
    itemsForShip,
    itemsForEmail,
    itemsGroupWithMulitplePickupMode
}) => (
    <Node {...LineItems}>
        <Node {...Header}>
            {heading}
            {editLink}
        </Node>
        {itemsGroupWithMulitplePickupMode === undefined && itemsForPickup && <LineItemGroupComponent {...itemsForPickup} />}
        {itemsGroupWithMulitplePickupMode === undefined && itemsForEmail && <LineItemGroupComponent {...itemsForEmail} />}
        {itemsGroupWithMulitplePickupMode === undefined && itemsForShip && <LineItemGroupComponent {...itemsForShip} />}
        {itemsGroupWithMulitplePickupMode !== undefined
            ? itemsGroupWithMulitplePickupMode.map((item, index) => {
                  return <LineItemGroupComponentWithMultiplePickUp {...item} key={index} />;
              })
            : null}
    </Node>
);

// const OrderSummaryComponent: React.FC<IOrderSummary> = ({ heading, lines }) => (
//     <div className='msc-order-summary-wrapper'>
//         {heading}
//         <div className='msc-order-summary__items'>
//             {lines && (
//                 <>
//                     {lines.subtotal}
//                     {lines.shipping}
//                     {lines.otherCharge}
//                     {lines.tax}
//                     {lines.totalDiscounts}
//                     {lines.loyalty}
//                     {lines.giftCard}
//                     {lines.orderTotal}
//                 </>
//             )}
//         </div>
//     </div>
// );

// const PaymentSummaryComponent: React.FC<IInvoicePaymentSummary> = ({ heading, lines }) => (
//     <div className='msc-invoice-summary-wrapper'>
//         {heading}
//         <div className='msc-invoice-summary__items'>
//             {lines && (
//                 <>
//                     {lines.invoices}
//                     {lines.giftCard}
//                     {lines.loyalty}
//                     {lines.orderTotal}
//                 </>
//             )}
//         </div>
//     </div>
// );

const CheckoutView: React.FC<ICheckoutViewProps> = props => {
    const {
        canShow,
        checkoutProps,
        headerProps,
        hasSalesOrder,
        bodyProps,
        mainProps,
        mainControlProps,
        orderConfirmation,
        loading,
        alert,
        title,
        // guidedForm,
        lineItems,
        placeOrderButton,
        isEmptyCart,
        context,
        config,
        resources
        // keepShoppingButton
    } = props;

    const telemetryContent = getTelemetryObject(props.context.request.telemetryPageName!, props.friendlyName, props.telemetry);
    const isAuthenticated = props.context.request.user.isAuthenticated;
    const registeredEmail = props.context.request?.user?.emailAddress;
    const [emailID, setEmailID] = React.useState<string>('');
    const [confirmEmailID, setConfirmEmailID] = React.useState<boolean>(false);
    const [EmailDownloadLink, setEmailDownloadLink] = React.useState<boolean>(false);
    const [inputError, setInputError] = React.useState<string>('');
    const [defaultCustomerDetails, setDefaultCustomerDetails] = React.useState<any>();
    const [disableEmailInputSection, setDisableEmailInputSection] = React.useState<boolean>(true);

    // Identifying Language
    const toggleLang = getToggleLang(context);
    const userPrefLang = getUserPrefLang(context.request.user.customerAccountNumber as any, context);
    const preferredLanguage: any = getUserPreferredLanguage(config.siteName as any, ACTION_BUTTONS, userPrefLang, toggleLang, context);
    const preferredKeteSiteLanguage: any = getUserPreferredLanguage(
        config.siteName,
        DOWNLOAD_KETE_SITE_LANGUAGE,
        userPrefLang,
        toggleLang,
        context
    );
    React.useEffect(() => {
        void (async () => {
            var customerDetails = await getDefaultCustomerDetails();
            if (customerDetails) {
                setDefaultCustomerDetails(customerDetails);
                await updateShippingAddress(customerDetails);
            }
            if (isAuthenticated && registeredEmail) {
                await updateGuestProfile(registeredEmail);
                await (await props.data.cart).updateReceiptEmail({ newEmail: registeredEmail });
                setEmailID(registeredEmail);
            } else {
                if (!EmailDownloadLink && customerDetails && customerDetails.Email) {
                    await updateGuestProfile(customerDetails.Email);
                    await (await props.data.cart).updateReceiptEmail({ newEmail: customerDetails.Email });
                }
            }
        })();
    }, [props?.data?.checkout?.result]);

    async function getDefaultCustomerDetails() {
        try {
            const context: any = { callerContext: props.context.actionContext };
            var result = await getDefaultCustomerDetailsAsync(context);
            console.log('GetDefaultCustomerDetails', result);
            return result;
        } catch (err) {
            console.log(err);
            return;
        }
    }

    async function fnEmailSelectionClick(): Promise<void> {
        if (fnValidateEmail(emailID)) {
            setInputError('');
            await updateGuestProfile(emailID);
            setConfirmEmailID(true);
        } else {
            setInputError(preferredKeteSiteLanguage === 'maori' ? resources.emailErrorMessageTextInMaori : resources.emailErrorMessageText);
        }
    }

    const updateGuestProfile = async (newGuestCheckoutEmail: string): Promise<void> => {
        if (props.data.checkout.result) {
            console.log('Email Update:', newGuestCheckoutEmail);
            await props.data.checkout.result.updateGuestCheckoutEmail({ newGuestCheckoutEmail });
        }
    };

    function fnEmailChangeClick(): void {
        setConfirmEmailID(false);
    }

    const updateShippingAddress = async (customerDetails: any): Promise<void> => {
        const newShippingAddress: Address = {
            Name: customerDetails.Name ? customerDetails.Name : 'User',
            Id: '',
            FullAddress: customerDetails.Address,
            RecordId: customerDetails.Id,
            Street: customerDetails.Street,
            StreetNumber: '',
            County: '',
            CountyName: '',
            City: customerDetails.City,
            DistrictName: '',
            State: customerDetails.State,
            StateName: customerDetails.StateName,
            ZipCode: customerDetails.ZipCode,
            ThreeLetterISORegionName: customerDetails.CountryRegionId,
            Phone: '',
            PhoneRecordId: 0,
            PhoneExt: '',
            Email: '',
            EmailContent: '',
            EmailRecordId: 0,
            Url: '',
            UrlRecordId: 0,
            TwoLetterISORegionName: 'US',
            Deactivate: false,
            AttentionTo: '',
            BuildingCompliment: '',
            Postbox: '',
            TaxGroup: '',
            AddressTypeValue: 6,
            IsPrimary: false,
            IsPrivate: false,
            PartyNumber: '',
            IsAsyncAddress: false,
            DirectoryPartyTableRecordId: 0,
            DirectoryPartyLocationRecordId: 0,
            DirectoryPartyLocationRoleRecordId: 0,
            LogisticsLocationId: '',
            LogisticsLocationRecordId: 0,
            LogisticsLocationExtRecordId: 0,
            LogisticsLocationRoleRecordId: 0,
            PhoneLogisticsLocationRecordId: 0,
            PhoneLogisticsLocationId: '',
            EmailLogisticsLocationRecordId: 0,
            EmailLogisticsLocationId: '',
            UrlLogisticsLocationRecordId: 0,
            UrlLogisticsLocationId: '',
            ExpireRecordId: 0,
            SortOrder: 0,
            RoleCount: 0,
            DataAreaId: '',
            ExtensionProperties: []
        };

        const updateCartDeliverySpecificationInput = {
            deliveryModeId: '99',
            shippingAddress: newShippingAddress,
            channelDeliveryOptionConfig: undefined
        };

        if (props.data.checkout.result) {
            console.log('--------updateShippingAddress-------');
            await props.data.checkout.result.updateShippingAddress({ newShippingAddress });
            await props.data.checkout.result.checkoutCart.updateShippingAddress({ newShippingAddress }).catch(error => {
                props.telemetry.error(error);
            });
            await props.data.checkout.result.checkoutCart.updateCartDeliverySpecification(updateCartDeliverySpecificationInput);
        }
    };

    function renderEmailInputSection() {
        const defaultBtnBackground = getAsset('images/esl_default.png', props.context.request);

        return (
            <>
                <div tabIndex={0} className={`moe-checkout-confirm ${disableEmailInputSection ? 'disabled' : ''}`}>
                    <h3 className='moe-checkout-confirm__header'>
                        {preferredKeteSiteLanguage === 'maori'
                            ? resources.confirmYourSelectionTextInMaori
                            : resources.confirmYourSelectionText}
                    </h3>
                    <div className='moe-checkout-confirm__content'>
                        {!isAuthenticated && (
                            <div className='moe-checkout-confirm__signin'>
                                <p>{preferredKeteSiteLanguage === 'maori' ? resources.signInTextInMaori : resources.signInText}</p>
                                {
                                    <SignInButton
                                        text={preferredLanguage === 'maori' ? resources.logInTextInMaori : resources.logInText}
                                        ariaLabel={preferredLanguage === 'maori' ? resources.logInTextInMaori : resources.logInText}
                                        href={props.context.request.user.signInUrl!}
                                        telemetryContent={telemetryContent}
                                        imageURL={props?.config?.sigInBackgroundImage?.src || defaultBtnBackground}
                                    />
                                }
                            </div>
                        )}
                        <div className='moe-checkout-confirm__email'>
                            {!isAuthenticated ? (
                                <>
                                    <p>
                                        {preferredKeteSiteLanguage === 'maori' ? resources.eslAccountTextInMaori : resources.eslAccountText}
                                    </p>
                                    <input
                                        type='text'
                                        disabled={confirmEmailID}
                                        id='download-email'
                                        onChange={e => setEmailID(e.target.value)}
                                        // value={emailID}
                                    ></input>
                                    <span className='moe-form__validation'>{inputError}</span>
                                </>
                            ) : (
                                <>
                                    <p>
                                        {preferredKeteSiteLanguage === 'maori'
                                            ? resources.downloadLinkTextInMaori
                                            : resources.downloadLinkText}
                                    </p>
                                    {registeredEmail && (
                                        <label>{`${registeredEmail} ${
                                            preferredKeteSiteLanguage === 'maori'
                                                ? resources.registeredEmailTextInMaori
                                                : resources.registeredEmailText
                                        }`}</label>
                                    )}
                                </>
                            )}
                        </div>
                        {!isAuthenticated && (
                            <div className='moe-checkout-confirm__actions'>
                                {!confirmEmailID ? (
                                    <button
                                        disabled={disableEmailInputSection}
                                        className='moe-btn moe-btn--primary-blue'
                                        title={
                                            preferredLanguage === 'maori' ? resources.confirmEmailTextInMaori : resources.confirmEmailText
                                        }
                                        onClick={() => fnEmailSelectionClick()}
                                    >
                                        {preferredLanguage === 'maori' ? resources.confirmEmailTextInMaori : resources.confirmEmailText}
                                    </button>
                                ) : (
                                    <button
                                        className='moe-btn moe-btn--primary-blue'
                                        title={preferredLanguage === 'maori' ? resources.changeEmailTextInMaori : resources.changeEmailText}
                                        onClick={() => fnEmailChangeClick()}
                                    >
                                        {preferredLanguage === 'maori' ? resources.changeEmailTextInMaori : resources.changeEmailText}
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </>
        );
    }

    async function fnCheckEmailDownloadLink(checked: boolean) {
        setEmailDownloadLink(checked);
        setDisableEmailInputSection(!checked);
        const extensionProoerites: CommerceProperty[] = [{ Key: 'EmailDownloadLink', Value: { BooleanValue: checked } }];
        await props.data.checkout.result?.checkoutCart.updateExtensionProperties({ newExtensionProperties: extensionProoerites });

        if (!checked) {
            const email: string = isAuthenticated ? registeredEmail : defaultCustomerDetails.Email;
            await updateGuestProfile(email);
            await (await props.data.cart).updateReceiptEmail({ newEmail: email });
        } else {
            const email: string = (isAuthenticated ? registeredEmail : emailID) || '';
            await updateGuestProfile(email);
            await (await props.data.cart).updateReceiptEmail({ newEmail: email });
        }
    }

    function fnValidateEmail(mail: string) {
        if (/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(mail)) {
            return true;
        }
        return false;
    }

    return (
        <Module {...checkoutProps}>
            {!hasSalesOrder && <Node {...headerProps}>{title}</Node>}
            {!hasSalesOrder && !isEmptyCart && (
                <Node {...bodyProps}>
                    {loading}
                    {alert}
                    {canShow && (
                        <>
                            <Node {...mainProps}>
                                <div tabIndex={0} className='ms-checkout-wrap moe-checkout-wrap'>
                                    {lineItems && <LineItemsComponent {...lineItems} />}

                                    <Node {...mainControlProps}>
                                        <div className='moe-form__control moe-form__control--checkout'>
                                            <input
                                                className='moe-form__checkbox'
                                                type='checkbox'
                                                id='chkDownloadArtiact'
                                                onChange={e => {
                                                    void fnCheckEmailDownloadLink(e.target.checked);
                                                }}
                                                aria-label='Email Checkbox'
                                                // checked={EmailDownloadLink}
                                            ></input>
                                            <label className='moe-form__label' label-for='chkDownloadArtiact'>
                                                {preferredKeteSiteLanguage === 'maori'
                                                    ? resources.emailLinkCheckboxTextInMaori
                                                    : resources.emailLinkCheckboxText}
                                            </label>
                                        </div>
                                        {placeOrderButton}
                                    </Node>
                                </div>
                                {renderEmailInputSection()}
                            </Node>
                        </>
                    )}
                </Node>
            )}
            {hasSalesOrder && orderConfirmation}
        </Module>
    );
};

export default CheckoutView;
